import React, { useEffect, useRef, useState } from "react";
import { capture } from "../../../analytics";
import { gql, useQuery, useMutation } from "@apollo/client";
import {
  getStoredInviteCode,
  clearStoredInviteCode,
} from "../../../referral";

const MY_SUBSCRIPTION_STATUS = gql`
  query MySubscriptionStatusForBanner {
    mySubscriptionStatus {
      hasActiveSubscription
    }
  }
`;

const MY_ACCESS = gql`
  query MyAccessForBanner {
    myAccess {
      member
      paywalled
    }
  }
`;

const CREATE_MY_CHECKOUT_SESSION = gql`
  mutation CreateMyCheckoutSessionForBanner($returnUrl: String!) {
    createMyCheckoutSession(returnUrl: $returnUrl)
  }
`;

// Season Pass (epic #307 phase 1). `available` stays false until the
// one-time price is configured server-side, so the extra button only
// appears once David opens sales.
const MY_SEASON_PASS = gql`
  query MySeasonPassForBanner {
    mySeasonPass {
      available
      priceCents
      currency
      active
      until
      daysLeft
    }
  }
`;

const CREATE_MY_SEASON_PASS_CHECKOUT = gql`
  mutation CreateMySeasonPassCheckoutForBanner($returnUrl: String!) {
    createMySeasonPassCheckoutSession(returnUrl: $returnUrl)
  }
`;

const fmtPassPrice = (cents, currency) => {
  if (cents == null) return "";
  const amt = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  return `$${amt}${currency && currency !== "usd" ? " " + currency.toUpperCase() : ""}`;
};

const fmtPassDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const REDEEM_INVITE_CODE = gql`
  mutation RedeemInviteCodeFromBanner($code: String!) {
    redeemInviteCode(code: $code) {
      ok
      message
      member
    }
  }
`;

// Compact CTA shown at the top of the Events tab for users who don't
// currently have an active Stripe subscription. States:
//   - sex unanswered -> "finish your profile" prompt (opens the
//     t-shirt/sex modal via onNeedInfo); the subscribe button stays
//     hidden until the question is answered.
//   - sex === "female":
//       * member standing (approved / legacy free access) -> nothing
//       * ladiesPaywall flag ON and not yet a member -> the same
//         Subscribe button everyone sees, plus "Have an invite code?"
//         (a code from /signup?code=… auto-redeems on first load)
//       * flag OFF -> nothing (women-free policy, legacy behavior)
//   - sex === "male"  -> Subscribe button (Stripe Checkout).
//   - a live Season Pass (#307) -> a quiet "your season runs through
//     <date>" line instead of any subscribe button; when the pass is
//     on sale and the caller has none, a Season Pass button sits next
//     to Subscribe.
// Auto-hides entirely for active subscribers.
const SubscribeBanner = ({ userInfo, onNeedInfo }) => {
  const { data, loading } = useQuery(MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: "network-only",
  });
  const {
    data: accessData,
    loading: accessLoading,
    refetch: refetchAccess,
  } = useQuery(MY_ACCESS, { fetchPolicy: "network-only" });
  const [createCheckout, { loading: checkoutLoading }] = useMutation(
    CREATE_MY_CHECKOUT_SESSION
  );
  const { data: passData } = useQuery(MY_SEASON_PASS, {
    fetchPolicy: "network-only",
  });
  const [createPassCheckout, { loading: passLoading }] = useMutation(
    CREATE_MY_SEASON_PASS_CHECKOUT
  );
  const [redeemCode, { loading: redeeming }] = useMutation(REDEEM_INVITE_CODE);
  const [error, setError] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [codeNote, setCodeNote] = useState("");
  const autoTried = useRef(false);

  const sex = String(userInfo?.sex || "").trim().toLowerCase();
  const member = accessData?.myAccess?.member === true;
  const paywalled = accessData?.myAccess?.paywalled === true;

  const submitCode = async (code) => {
    const clean = String(code || "").trim().toUpperCase();
    if (!clean) return;
    setError("");
    setCodeNote("");
    try {
      const { data: out } = await redeemCode({ variables: { code: clean } });
      const r = out?.redeemInviteCode;
      if (r?.ok) {
        clearStoredInviteCode();
        capture("invite_code_redeemed", { member: !!r.member });
        setCodeNote(r.message || "Code applied.");
        if (r.member) {
          // Standing changed server-side; a full reload re-reads
          // events/locations/chat with the new access.
          setTimeout(() => window.location.reload(), 900);
        } else {
          refetchAccess();
        }
      } else {
        setCodeNote(r?.message || "That code didn't work.");
      }
    } catch (err) {
      setCodeNote(err.message || "Couldn't apply that code.");
    }
  };

  // Auto-redeem a code captured on /signup?code=… — once, as soon as
  // we know the person isn't already a member.
  useEffect(() => {
    if (autoTried.current || accessLoading || !accessData) return;
    const stored = getStoredInviteCode();
    if (!stored) return;
    autoTried.current = true;
    if (member) {
      clearStoredInviteCode();
      return;
    }
    submitCode(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLoading, accessData, member]);

  if (loading || !data) return null;
  if (data.mySubscriptionStatus?.hasActiveSubscription) return null;

  const pass = passData?.mySeasonPass || null;
  // Live Season Pass: they already have standing for the season, so
  // no subscribe nudge — just when it ends and the member upsell.
  if (pass?.active) {
    return (
      <div
        style={{
          marginBottom: 10,
          padding: "6px 12px",
          background: "#eef7ee",
          border: "1px solid #b9dcb5",
          borderRadius: 8,
          color: "#1f4f1d",
          fontSize: 13,
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontWeight: 700 }}>
          <span role="img" aria-label="ticket">🎟️</span> Season Pass · through{" "}
          {fmtPassDate(pass.until)}
          {pass.daysLeft != null && pass.daysLeft <= 14
            ? ` (${pass.daysLeft} day${pass.daysLeft === 1 ? "" : "s"} left)`
            : ""}
        </span>
        <span style={{ color: "#3a5a38" }}>
          Members get the next season free.
        </span>
      </div>
    );
  }

  // Women: free access is the legacy rule; under the paywall flag a
  // non-member woman sees the same subscribe CTA plus the code box.
  if (sex === "female") {
    if (accessLoading || !accessData) return null;
    if (member || !paywalled) return null;
  }

  // Not answered yet (legacy rows may carry "default") — prompt for
  // the profile questions instead of showing a subscribe button.
  if (sex !== "male" && sex !== "female") {
    return (
      <div
        style={{
          marginBottom: 10,
          padding: "6px 12px",
          background: "#eef4fb",
          border: "1px solid #bcd4ef",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span style={{ color: "#1d3d5f", fontSize: 13, fontWeight: 600 }}>
          Finish setting up your profile
        </span>
        <button
          type="button"
          onClick={() => onNeedInfo && onNeedInfo()}
          style={{
            padding: "5px 12px",
            background: "#1f6feb",
            color: "white",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Get started
        </button>
      </div>
    );
  }

  const goCheckout = async () => {
    capture("checkout_started", { source: "events_banner" });
    setError("");
    const returnUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : "https://activebeaches.com/dashboard";
    try {
      const { data: out } = await createCheckout({ variables: { returnUrl } });
      const url = out?.createMyCheckoutSession;
      if (url) {
        window.location.href = url;
      } else {
        setError("Stripe returned no checkout URL.");
      }
    } catch (err) {
      setError(err.message || "Couldn't start checkout.");
    }
  };

  const goPassCheckout = async () => {
    capture("season_pass_checkout_started", { source: "events_banner" });
    setError("");
    const returnUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : "https://activebeaches.com/dashboard";
    try {
      const { data: out } = await createPassCheckout({ variables: { returnUrl } });
      const url = out?.createMySeasonPassCheckoutSession;
      if (url) {
        window.location.href = url;
      } else {
        setError("Stripe returned no checkout URL.");
      }
    } catch (err) {
      setError(err.message || "Couldn't start checkout.");
    }
  };

  const showCodeBox = sex === "female" && paywalled && !member;
  const showPassButton = pass?.available === true;
  const passPrice = fmtPassPrice(pass?.priceCents, pass?.currency);

  return (
    <div
      style={{
        marginBottom: 10,
        padding: "6px 12px",
        background: "#eef7ee",
        border: "1px solid #b9dcb5",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{ color: "#1f4f1d", fontSize: 13, fontWeight: 600, flex: "1 1 auto" }}
          title="See private locations, RSVPs, and our GroupMe chat."
        >
          Subscribe to unlock event details
        </span>
        <button
          type="button"
          onClick={goCheckout}
          disabled={checkoutLoading}
          style={{
            padding: "5px 12px",
            background: "#1f7a3a",
            color: "white",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: checkoutLoading ? "default" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {checkoutLoading ? "Opening…" : "Subscribe · $20/mo"}
        </button>
        {showPassButton && (
          <button
            type="button"
            onClick={goPassCheckout}
            disabled={passLoading}
            title="One payment for the whole season — tourney nights, standings, and the chat. No recurring charge."
            style={{
              padding: "5px 12px",
              background: "#fff",
              color: "#1f7a3a",
              border: "1px solid #1f7a3a",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: passLoading ? "default" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {passLoading
              ? "Opening…"
              : `Season Pass${passPrice ? " · " + passPrice : ""}`}
          </button>
        )}
      </div>
      {showCodeBox && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitCode(codeInput);
          }}
          style={{
            marginTop: 8,
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12.5, color: "#3a4150" }}>
            Have an invite code?
          </span>
          <input
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            placeholder="ABCD2345"
            maxLength={16}
            style={{
              padding: "5px 8px",
              border: "1px solid #c9d0dc",
              borderRadius: 6,
              fontSize: 13,
              letterSpacing: "0.08em",
              width: 130,
              textTransform: "uppercase",
            }}
          />
          <button
            type="submit"
            disabled={redeeming || !codeInput.trim()}
            style={{
              padding: "5px 12px",
              background: "#fff",
              color: "#1f7a3a",
              border: "1px solid #1f7a3a",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {redeeming ? "Checking…" : "Apply"}
          </button>
          {codeNote && (
            <span style={{ fontSize: 12.5, color: "#1f4f1d" }}>{codeNote}</span>
          )}
        </form>
      )}
      {error && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: "#b02a2a" }}>{error}</div>
      )}
    </div>
  );
};

export default SubscribeBanner;
